import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ToastController, ToastOptions } from '@ionic/angular';
import { replace, toString } from 'lodash';
import { Subject, takeUntil } from 'rxjs';
import { EventService } from '@providers/event/event.service';
import { Logger } from '../../utils/logger';

/**
 * Toast Component.
 *
 * @exports
 * @class ToastComponent
 */
@Component({
  selector: 'app-toast',
  template: ``
})
export class ToastComponent implements OnInit, OnDestroy {
  private readonly event = inject(EventService);
  private readonly toast = inject(ToastController);

  private readonly logger = Logger.getInstance(ToastComponent.name);
  customOptions: ToastOptions | undefined = undefined;

  message: string;

  options: ToastOptions = {
    position: 'top',
    duration: 5000,
    buttons: [{ text: 'Close', role: 'cancel' }],
    layout: 'baseline'
  };

  toastErrorQueue: {
    list: Array<string>;
    queue: number;
    status: 'running' | 'stop';
  } = {
    list: [],
    queue: -1,
    status: 'stop'
  };

  destroyed = new Subject<boolean>();

  /**
   * Creates an instance of ToastComponent.
   *
   * @memberof ToastComponent
   */
  constructor() {
    this.message = ``;
  }

  /**
   * Prepare data.
   */
  ngOnInit(): void {
    this.setup();
  }

  /**
   * Cleanup.
   */
  ngOnDestroy(): void {
    this.destroyed.next(true);
    this.destroyed.complete();
  }

  /**
   * Add toast error to queue.
   *
   * @param {Error | string} error Message.
   * @returns {void}
   */
  addToastErrorToQueue(error: Error | string): void {
    const message: string = this.getMessage(error);
    const isAlreadyExists = this.toastErrorQueue.list.some((item) => {
      const reg = new RegExp(message, 'gim');
      return reg.test(item);
    });
    if (isAlreadyExists) {
      return;
    }

    this.toastErrorQueue.list.push(message);
    this.runToastErrorQueue();
  }

  /**
   * Get toast message.
   *
   * @param {string} msg Message.
   * @returns {string}
   */
  getMessage(msg: any): string {
    if (typeof msg !== 'string') {
      msg = toString(msg?.msg ?? msg?.reject ?? msg?.error);
    }
    return replace(msg, /Error: |GraphQLError: |ValidationError: /gi, '');
  }

  /**
   * On toast error did dimiss.
   *
   * @returns {void}
   */
  onToastErrorDidDismiss(): void {
    if (this.toastErrorQueue.queue >= 0) {
      this.toastErrorQueue.list.splice(this.toastErrorQueue.queue, 1);
      this.toastErrorQueue.queue = -1;
    }
    setTimeout(() => {
      this.toastErrorQueue.status = 'stop';
      this.runToastErrorQueue();
    }, 10);
  }

  /**
   * Present Success Toast.
   *
   * @param {unknown} message Toast-message.
   * @param {ToastOptions} options Options.
   * @returns {Promise<void>}
   */
  async presentInfoToast(message: any, options?: ToastOptions): Promise<void> {
    if (!message) return;

    const toast = await this.toast.create({
      ...this.options,
      ...(options ?? {}),
      cssClass: 'ion-toast-info',
      message: this.getMessage(message)
    });

    await toast.present();
  }

  /**
   * Present Success Toast.
   *
   * @param {unknown} message Toast-message.
   * @returns {Promise<void>}
   */
  async presentSuccessToast(message: any): Promise<void> {
    const logger = this.logger.child(this.presentToast.name);
    if (!message) return;

    const opts = this.customOptions
      ? {
          ...this.options,
          ...this.customOptions
        }
      : {
          ...this.options,
          color: 'toast-success',
          message: this.getMessage(message),
          cssClass: 'ion-toast-text-white'
        };

    const toast = await this.toast.create(opts);

    toast
      .onDidDismiss()
      .then(() => {
        this.customOptions = undefined;
      })
      .catch(logger.error);

    await toast.present();
  }

  /**
   * Present Toast.
   *
   * @param {unknown} message Toast-message.
   * @returns {Promise<void>}
   */
  async presentToast(message: any): Promise<void> {
    const logger = this.logger.child(this.presentToast.name);

    if (!message) return;
    if (typeof message !== 'string') {
      message = toString(message);
    }

    const info = this.getMessage(message);

    if (info === this.message) return;

    const toast = await this.toast.create({
      ...this.options,
      color: 'toast-info',
      message: info,
      cssClass: 'ion-toast-text-white'
    });

    this.message = info;

    toast
      .onWillDismiss()
      .then(() => {
        this.message = ``;
      })
      .catch(logger.error);

    await toast.present();
  }

  /**
   * RunToastErrorQueue.
   *
   * @returns {void}
   */
  async runToastErrorQueue(): Promise<void> {
    const logger = this.logger.child(this.runToastErrorQueue.name);

    if (this.toastErrorQueue.list.length <= 0) {
      return;
    }
    if (this.toastErrorQueue.status === 'running') {
      return;
    }

    this.toastErrorQueue.queue = 0;
    const error = this.toastErrorQueue.list[0];
    this.toastErrorQueue.status = 'running';

    const opts: ToastOptions = this.customOptions
      ? {
          ...this.options,
          ...this.customOptions
        }
      : {
          ...this.options,
          color: 'toast-failed',
          message: error,
          cssClass: 'ion-toast-text-white'
        };

    const toast = await this.toast.create(opts);

    toast
      .onDidDismiss()
      .then(() => {
        this.customOptions = undefined;
        this.onToastErrorDidDismiss();
      })
      .catch(logger.error);

    await toast.present();
  }

  /**
   * Setup toast.
   *
   * @memberof ToastComponent
   */
  setup(): void {
    this.event
      .listen('TOAST_INFO')
      .pipe(takeUntil(this.destroyed))
      .subscribe({
        /**
         * On next.
         *
         * @param {object} payload Payload.
         */
        next: (payload): void => {
          this.presentInfoToast(payload.message, payload.options);
        }
      });
    this.event
      .listen(this.event.keys.TOAST_SUCCESS)
      .pipe(takeUntil(this.destroyed))
      .subscribe({
        /**
         * On next.
         *
         * @param {object} payload Payload.
         */
        next: (payload): void => {
          if (
            !/required authenticated user/i.test(payload.message ?? payload)
          ) {
            if (payload.message) {
              this.customOptions = payload;
            }
            this.presentSuccessToast(payload.message ?? payload);
          }
        }
      });
    this.event
      .listen(this.event.keys.TOAST_ERROR)
      .pipe(takeUntil(this.destroyed))
      .subscribe({
        /**
         * On next.
         *
         * @param {object} payload Payload.
         */
        next: (payload): void => {
          if (
            !/required authenticated user/i.test(payload.message ?? payload)
          ) {
            if (payload.message) {
              this.customOptions = payload;
            }
            this.addToastErrorToQueue(payload.message ?? payload);
          }
        }
      });
    this.event
      .listen(this.event.keys.TOAST)
      .pipe(takeUntil(this.destroyed))
      .subscribe({
        /**
         * On next.
         *
         * @param {string} message Message.
         */
        next: (message: string): void => {
          if (!/required authenticated user/i.test(message)) {
            this.presentToast(message);
          }
        }
      });
  }
}
